import { useParams } from '@tanstack/react-router';
import { useCollectionSources, useSource } from '_/hooks/useSources';
import { ISource } from '_/types/sources/source.types';
import React, { createContext, useContext, ReactNode, useMemo } from 'react';

import { useCollection } from './CollectionContext';

interface SourceContextValue {
  sources: ISource[];
  selectedSource: ISource | null;
  selectedSourceSlug: string | null;
  isLoading: boolean;
  refetch: () => void;
}

const SourceContext = createContext<SourceContextValue | undefined>(undefined);

export function SourceProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as Partial<{ sourceSlug?: string }>;
  const sourceSlug = params.sourceSlug;
  const { selectedCollection } = useCollection();

  // Fetch all sources (for lists, sidebars, etc.)
  const {
    data: sources = [],
    isLoading: isLoadingSources,
    refetch: refetchSources,
  } = useCollectionSources(selectedCollection?.id);

  // Fetch individual source directly if we have a specific slug (faster!)
  const shouldFetchIndividualSource = sourceSlug && sourceSlug !== '_new';
  const {
    data: individualSource,
    isLoading: isLoadingIndividualSource,
    refetch: refetchIndividualSource,
  } = useSource(selectedCollection?.id, shouldFetchIndividualSource ? sourceSlug : undefined);

  // Use individual source if available (faster), otherwise fall back to finding in sources list
  const selectedSource = useMemo(() => {
    // Don't try to find sources for special route slugs
    if (!sourceSlug || sourceSlug === '_new') return null;

    // If we have the individual source, use it
    if (individualSource) return individualSource;

    // Fall back to searching in sources list
    return sources.find((source) => source.slug === sourceSlug) || null;
  }, [sources, sourceSlug, individualSource]);

  // Loading is true if we're loading the individual source OR (if no individual source query) loading sources list
  const isLoading = shouldFetchIndividualSource ? isLoadingIndividualSource : isLoadingSources;

  const refetch = () => {
    refetchSources();
    if (shouldFetchIndividualSource) {
      refetchIndividualSource();
    }
  };

  const value: SourceContextValue = {
    sources: sources,
    selectedSource: selectedSource,
    selectedSourceSlug: sourceSlug || null,
    isLoading: isLoading,
    refetch: refetch,
  };

  return <SourceContext.Provider value={value}>{children}</SourceContext.Provider>;
}

export function useSourceContext(): SourceContextValue {
  const context = useContext(SourceContext);
  if (context === undefined) {
    throw new Error('useSourceContext must be used within a SourceProvider');
  }
  return context;
}

/**
 * Safe version of useSourceContext that returns null when used outside SourceProvider.
 * Use this when SourceContext may not be available.
 */
export function useSourceContextSafe(): SourceContextValue | null {
  const context = useContext(SourceContext);
  return context === undefined ? null : context;
}
